import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ApiError,
  listUsers,
  logout,
  me as loadMe,
  onAuthRequired,
  onReauthRequired,
  onSessionLocked,
} from '../api/client';
import type { Me } from '../api/schemas';
import { LoginPage } from '../auth/LoginPage';
import { useEinrichtungslage } from '../einstellungen/einrichtungslage';
import { Sperrbildschirm } from '../auth/Sperrbildschirm';
import {
  LEERLAUF_MS,
  rueckkehrpfadHolen,
  rueckkehrpfadMerken,
  useLeerlauf,
} from '../auth/sitzungslage';
import { Loading } from '../ui/elements';
import type { Verzeichnis } from '../users/verzeichnis';
import { Shell } from './Shell';
import { istAuswertungsadresse, useNavigation, type Route } from './router';
import { SplashScreen } from './splash/SplashScreen';
import {
  splashFaelligLesen,
  splashFaelligMerken,
  splashMarkeVerwerfen,
} from './splash/sitzungsmarke';

/**
 * Die Fassungszeile für das Startbild — aus den Bündel-Konstanten, die
 * `apps/web/vite.config.ts` einbettet (Auftrag `W-versionbundle`, Befund
 * `W-splash` 2026-09-10: das Browser-Bündel kannte seine eigene Herkunft bis
 * dahin gar nicht). Einmal beim Laden des Moduls gebildet und nicht bei jedem
 * Rendern neu — die drei Werte sind zur Bauzeit fest, ein `useMemo` dafür
 * wäre ein Umweg ohne Nutzen.
 */
const SPLASH_FASSUNGSZEILE = `${__FA_VERSION__} · ${__FA_COMMIT__}`;

/**
 * Die Seiten sind route-basiert aufgeteilt (`React.lazy`), die Schale nicht.
 *
 * **Warum hier und nicht woanders.** Vor dem Aufteilen lag das Hauptbündel bei
 * 747 kB — jede der 25 Seiten kam mit, egal welche Adresse zuerst geladen
 * wurde. `Sidebar`, `Kopfzeile` und `LoginPage` bleiben eager: Sie gehören zum
 * ersten sichtbaren Bild (angemeldet wie nicht), ein eigener Ladeschritt dafür
 * verzögerte nur.
 *
 * **Der Name als zweiter Import je Zeile, nicht `export default`.** Jede Seite
 * behält ihren benannten Export — das Modul selbst ändert sich nicht, nur wie
 * `App.tsx` es lädt. `.then((modul) => ({ default: modul.X }))` ist die
 * Übersetzung, die `React.lazy` dafür braucht.
 */
const AccountsPage = lazy(() =>
  import('../accounts/AccountsPage').then((m) => ({ default: m.AccountsPage })),
);
const AccountLedgerPage = lazy(() =>
  import('../accounts/AccountLedgerPage').then((m) => ({ default: m.AccountLedgerPage })),
);
const BankAccountPage = lazy(() =>
  import('../bank/BankAccountPage').then((m) => ({ default: m.BankAccountPage })),
);
const BankAccountsPage = lazy(() =>
  import('../bank/BankAccountsPage').then((m) => ({ default: m.BankAccountsPage })),
);
const BankImportPage = lazy(() =>
  import('../bank/BankImportPage').then((m) => ({ default: m.BankImportPage })),
);
const BankStatementsPage = lazy(() =>
  import('../bank/BankStatementsPage').then((m) => ({ default: m.BankStatementsPage })),
);
const CustomerForm = lazy(() =>
  import('../customers/CustomerForm').then((m) => ({ default: m.CustomerForm })),
);
const CustomerPage = lazy(() =>
  import('../customers/CustomerPage').then((m) => ({ default: m.CustomerPage })),
);
const CustomersPage = lazy(() =>
  import('../customers/CustomersPage').then((m) => ({ default: m.CustomersPage })),
);
const DuplicatesPage = lazy(() =>
  import('../customers/DuplicatesPage').then((m) => ({ default: m.DuplicatesPage })),
);
const DocumentForm = lazy(() =>
  import('../documents/DocumentForm').then((m) => ({ default: m.DocumentForm })),
);
const DocumentPage = lazy(() =>
  import('../documents/DocumentPage').then((m) => ({ default: m.DocumentPage })),
);
const DocumentsPage = lazy(() =>
  import('../documents/DocumentsPage').then((m) => ({ default: m.DocumentsPage })),
);
const AborechnungenPage = lazy(() =>
  import('../aborechnungen/AborechnungenPage').then((m) => ({ default: m.AborechnungenPage })),
);
const AborechnungPage = lazy(() =>
  import('../aborechnungen/AborechnungPage').then((m) => ({ default: m.AborechnungPage })),
);
const AborechnungForm = lazy(() =>
  import('../aborechnungen/AborechnungForm').then((m) => ({ default: m.AborechnungForm })),
);
const IssuerPage = lazy(() =>
  import('../issuer/IssuerPage').then((m) => ({ default: m.IssuerPage })),
);
const BelegvorlagenPage = lazy(() =>
  import('../belegvorlagen/BelegvorlagenPage').then((m) => ({ default: m.BelegvorlagenPage })),
);
const MailvorlagenPage = lazy(() =>
  import('../mailvorlagen/MailvorlagenPage').then((m) => ({ default: m.MailvorlagenPage })),
);
const EntryPage = lazy(() =>
  import('../journal/EntryPage').then((m) => ({ default: m.EntryPage })),
);
const JournalPage = lazy(() =>
  import('../journal/JournalPage').then((m) => ({ default: m.JournalPage })),
);
const NumberRangesPage = lazy(() =>
  import('../number-ranges/NumberRangesPage').then((m) => ({ default: m.NumberRangesPage })),
);
const ProfilePage = lazy(() =>
  import('../profil/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const PeriodsPage = lazy(() =>
  import('../periods/PeriodsPage').then((m) => ({ default: m.PeriodsPage })),
);
const SuchePage = lazy(() =>
  import('../suche/SuchePage').then((m) => ({ default: m.SuchePage })),
);
const UebersichtPage = lazy(() =>
  import('../uebersicht/UebersichtPage').then((m) => ({ default: m.UebersichtPage })),
);
const VendorForm = lazy(() =>
  import('../vendors/VendorForm').then((m) => ({ default: m.VendorForm })),
);
const VendorPage = lazy(() =>
  import('../vendors/VendorPage').then((m) => ({ default: m.VendorPage })),
);
const VendorsPage = lazy(() =>
  import('../vendors/VendorsPage').then((m) => ({ default: m.VendorsPage })),
);
const EingangskorbPage = lazy(() =>
  import('../eingangsrechnungen/EingangskorbPage').then((m) => ({ default: m.EingangskorbPage })),
);
const ErfassungPage = lazy(() =>
  import('../eingangsrechnungen/ErfassungPage').then((m) => ({ default: m.ErfassungPage })),
);
const KreditorenerwartungPage = lazy(() =>
  import('../eingangsrechnungen/KreditorenerwartungPage').then((m) => ({
    default: m.KreditorenerwartungPage,
  })),
);
const AbrufPage = lazy(() =>
  import('../zahldienste/AbrufPage').then((m) => ({ default: m.AbrufPage })),
);
const Klaerliste = lazy(() =>
  import('../zahldienste/Klaerliste').then((m) => ({ default: m.Klaerliste })),
);
const UnkontiertPage = lazy(() =>
  import('../kontierung/UnkontiertPage').then((m) => ({ default: m.UnkontiertPage })),
);
const KlaerungPage = lazy(() =>
  import('../zahlungen/KlaerungPage').then((m) => ({ default: m.KlaerungPage })),
);
const ZahlungFreigabePage = lazy(() =>
  import('../zahlungen/FreigabePage').then((m) => ({ default: m.FreigabePage })),
);
const SystemstatusPage = lazy(() =>
  import('../system/SystemstatusPage').then((m) => ({ default: m.SystemstatusPage })),
);
const RegelnPage = lazy(() =>
  import('../regeln/RegelnPage').then((m) => ({ default: m.RegelnPage })),
);
const AnfragenPage = lazy(() =>
  import('../system/AnfragenPage').then((m) => ({ default: m.AnfragenPage })),
);
const UsersPage = lazy(() =>
  import('../users/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const ApiTokensPage = lazy(() =>
  import('../apitokens/ApiTokensPage').then((m) => ({ default: m.ApiTokensPage })),
);
const LizenzPage = lazy(() =>
  import('../lizenz/LizenzPage').then((m) => ({ default: m.LizenzPage })),
);
const DatensicherungPage = lazy(() =>
  import('../sicherung/DatensicherungPage').then((m) => ({ default: m.DatensicherungPage })),
);
const WebhooksPage = lazy(() =>
  import('../webhooks/WebhooksPage').then((m) => ({ default: m.WebhooksPage })),
);
const PortalVorgaengePage = lazy(() =>
  import('../portal/PortalVorgaengePage').then((m) => ({ default: m.PortalVorgaengePage })),
);
const GeldkontoAnfangsbestandPage = lazy(() =>
  import('../kontierung/GeldkontoAnfangsbestandPage').then((m) => ({
    default: m.GeldkontoAnfangsbestandPage,
  })),
);
const SaldenvortraegePage = lazy(() =>
  import('../kontierung/SaldenvortraegePage').then((m) => ({ default: m.SaldenvortraegePage })),
);
const AbstimmungPage = lazy(() =>
  import('../guthaben/AbstimmungPage').then((m) => ({ default: m.AbstimmungPage })),
);
const AbstimmungDetailPage = lazy(() =>
  import('../guthaben/AbstimmungDetailPage').then((m) => ({ default: m.AbstimmungDetailPage })),
);
const OffenePostenPage = lazy(() =>
  import('../berichte/OffenePostenPage').then((m) => ({ default: m.OffenePostenPage })),
);
// Die acht Adressen der Auswertungen teilen sich EINE Seitenkomponente: Was
// sie unterscheidet, ist der Inhalt; Reiterleiste, Zeitraumschalter und
// Fussteil sind bei allen dieselben (Spec 3.2).
const AuswertungenIndexPage = lazy(() =>
  import('../auswertungen/AuswertungenIndexPage').then((m) => ({
    default: m.AuswertungenIndexPage,
  })),
);
const ErstattungenPage = lazy(() =>
  import('../guthaben/ErstattungenPage').then((m) => ({ default: m.ErstattungenPage })),
);
const KorrekturenPage = lazy(() =>
  import('../guthaben/KorrekturenPage').then((m) => ({ default: m.KorrekturenPage })),
);
const DatevExportPage = lazy(() =>
  import('../datev/DatevExportPage').then((m) => ({ default: m.DatevExportPage })),
);
// Die Dateiseite der Lastschrift-Aera (Spec 2026-09-15, Abschnitt 5, `D-16`) --
// nachgetragen am 2026-09-16: Route und Seite waren gelandet, die Verknuepfung
// hier fehlte, und die Adresse zeigte eine leere Flaeche (`LS-seitenroute`).
const LastschriftPage = lazy(() =>
  import('../lastschrift/LastschriftPage').then((m) => ({ default: m.LastschriftPage })),
);
// Der Einrichtungsassistent (`PF-assistent`, Task 11 des Plans Systemeinstellungen):
// bei offener Einrichtung die einzige Seite der Schale, danach der Änderungsmodus.
const EinrichtungPage = lazy(() =>
  import('../einstellungen/EinrichtungPage').then((m) => ({ default: m.EinrichtungPage })),
);

// Die Seite „Übernahme aus Lexware Office" (`LX-webseite`): nach dem Abschluss der Einrichtung.
const UebernahmePage = lazy(() =>
  import('../vorsystem/UebernahmePage').then((m) => ({ default: m.UebernahmePage })),
);

/** Die Adresse, auf die die Schale bei offener Einrichtung jede andere umbiegt. */
const EINRICHTUNG_ROUTE: Route = { name: 'einrichtung' };

/**
 * Der Einstieg: angemeldet oder nicht.
 *
 * **Die Frage beantwortet der Server, nicht der Browser.** Beim Start wird
 * `GET /auth/me` aufgerufen; eine 401 heisst «keine Sitzung» und führt zur
 * Anmeldemaske. Ein Merker im lokalen Speicher wäre schneller und wäre falsch:
 * Er sagte «angemeldet», nachdem die Sitzung serverseitig beendet wurde, und
 * die Oberfläche zeigte leere Tabellen statt einer Anmeldemaske.
 *
 * **Die Fähigkeiten aus `/auth/me` steuern die Navigation und sonst nichts.**
 * Sie sind Bequemlichkeit, kein Schutz: Über den Zugriff entscheidet der
 * Wächter bei jeder Anfrage neu.
 *
 * ---------------------------------------------------------------------------
 * VIER LAGEN STATT DREI — Auftrag `A-sitzungssperre` (2026-09-10)
 *
 * Zwei Meldungen des Auftraggebers laufen hier zusammen, und beide betreffen
 * denselben Punkt: **Was passiert, wenn die Sitzung nicht mehr trägt?**
 *
 * 1. «Wenn man nicht mehr eingeloggt ist, wird man nicht automatisch zum Login
 *    weitergeleitet, sondern bleibt im Backend und kann Unterseiten anklicken;
 *    Daten laden nicht mehr.» — Bis hierher reagierte diese Komponente auf
 *    genau **einen** Fehlercode (`PASSWORD_CHANGED_REAUTH_REQUIRED`); eine
 *    gewöhnliche `AUTH_REQUIRED` blieb bei der Maske, aus der sie kam, und
 *    landete dort in einer Fehlerbox. Wer sie zehnmal sah, hatte zehn
 *    Fehlerboxen und keine Anmeldemaske. Jetzt führt sie hierher.
 * 2. «Nach 60 Minuten Inaktivität nicht vollständig ausloggen, sondern ein
 *    Lock-Screen.» — Daraus wird die vierte Lage `locked`.
 *
 * **Die Lage `locked` verwirft den Anwendungsbaum NICHT.** Er bleibt montiert
 * und wird bloss unsichtbar geschaltet. Das ist die ganze Mechanik hinter der
 * Zusage des Sperrbildschirms — «Deine Eingaben und offenen Entwürfe bleiben
 * erhalten»: Es wird nichts weggeschrieben und nichts wiederhergestellt, der
 * Zustand jeder offenen Maske bleibt, wo er ist. Der Auftrag schlug dafür eine
 * Sicherung je Maske in `sessionStorage` vor; sie wäre für jede der Masken
 * einzeln zu verdrahten (die Erfassungsmaske allein führt über dreissig
 * `useState`-Werte), träfe damit immer nur die verdrahteten — und bekäme genau
 * für die drei genannten, was dieser Weg für **alle** liefert.
 *
 * **Die Grenze davon ist benannt und wird nicht verschwiegen: Ein Neuladen der
 * Seite im gesperrten Zustand verwirft offene Eingaben.** Dagegen hülfe nur
 * die Sicherung je Maske, und sie bleibt möglich — sie ist durch diesen Weg
 * nicht verbaut, nur nicht nötig für den Fall, den der Auftraggeber gemeldet
 * hat.
 *
 * **`hidden` heisst `display:none`, und das ist hier die Sicherheitsaussage.**
 * Der Anwendungsbaum ist unsichtbar, nicht fokussierbar und für eine
 * Sprachausgabe nicht vorhanden; hinter dem Schleier steht ein Gerüst ohne ein
 * einziges echtes Datum (`Sperrbildschirm.tsx`). Was er **nicht** leistet: Die
 * Daten stehen weiterhin im DOM und liessen sich mit den
 * Entwicklerwerkzeugen ansehen. Das ist keine neue Blösse — dieselbe Person
 * konnte dieselben Daten eine Minute vorher auf dem Bildschirm lesen —, und
 * die Sperre verspricht auch nichts anderes: Sie schützt gegen den
 * Vorübergehenden am unbeaufsichtigten Platz, nicht gegen den, der die
 * Tastatur wirklich in der Hand hat.
 */
export function App(): ReactNode {
  const [state, setState] = useState<'loading' | 'anonymous' | 'signed-in' | 'locked'>('loading');
  const [user, setUser] = useState<Me | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);
  /**
   * Derselbe Benutzer, aber **nur aus offenen Sitzungen** — für den
   * Anwendungsbaum.
   *
   * Zwei Zustände für eine Auskunft, und beide werden gebraucht:
   *
   * * `user` ist die jeweils frischeste Antwort auf `/auth/me`, auch die
   *   gesperrte. Der Sperrbildschirm liest daraus Name, Adresse,
   *   `gesperrtSeit` und `geraet`.
   * * `baumBenutzer` ist die letzte Antwort aus einer **offenen** Sitzung. Der
   *   Anwendungsbaum bekommt sie und nichts anderes: Die gesperrte Antwort
   *   trägt planmässig **leere** Fähigkeiten (der Server liefert sie so, siehe
   *   `auth.controller.ts`), und wer sie an `SignedIn` durchreichte, liesse
   *   hinter dem Schleier die halbe Navigation verschwinden und jeden Effekt,
   *   der an `capabilities` hängt, ein zweites Mal laufen.
   *
   * Er ist zugleich die Antwort auf «wer die Seite im gesperrten Zustand
   * öffnet». Dann ist er `undefined`, der Baum wird gar nicht erst montiert,
   * und es gibt auch nichts zu erhalten.
   */
  const [baumBenutzer, setBaumBenutzer] = useState<Me | undefined>(undefined);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const current = await loadMe();
      setUser(current);
      if (current.locked === true) {
        setState('locked');
        return;
      }
      setState('signed-in');
      setBaumBenutzer(current);
      setFailure(undefined);
      // Der gemerkte Pfad wird eingelöst, sobald wieder eine Sitzung steht.
      // `replaceState` und nicht `pushState`: Die Anmeldung ist kein Schritt,
      // zu dem der Zurück-Knopf zurückführen soll.
      const pfad = rueckkehrpfadHolen();
      if (pfad !== undefined && pfad !== window.location.pathname + window.location.search) {
        window.history.replaceState(null, '', pfad);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthenticated) {
        setUser(undefined);
        setState('anonymous');
        return;
      }
      setFailure(
        error instanceof ApiError
          ? error.message
          : 'Der Server ist nicht erreichbar. Bitte später erneut versuchen.',
      );
      setState('anonymous');
    }
  }, []);

  /**
   * Meldet den Fall, in dem irgendeine Anfrage — gleich auf welcher Seite —
   * mit `PASSWORD_CHANGED_REAUTH_REQUIRED` scheitert: Das Passwort ist
   * gewechselt, auf diesem Gerät oder, seit `C-pwsession`, serverseitig auf
   * einem zweiten. `api/client.ts` sieht als einzige Stelle jede Antwort der
   * API und meldet genau diesen einen Fehlerkode über `onReauthRequired` —
   * diese Komponente entscheidet, was daraus folgt, der Klient navigiert
   * nicht selbst.
   *
   * **Die Registrierung steht VOR `refresh()` im selben Effekt**, nicht in
   * einem zweiten: Andernfalls könnte das anfängliche `GET /auth/me` genau
   * diesen Fehler werfen, bevor die Anwendung zuhört, und die Meldung ginge
   * unter — dieselbe 401 liefe dann still über den `isUnauthenticated`-Zweig
   * unten durch, ohne den erklärenden Satz.
   *
   * **Kein neuer Zustand:** Es werden dieselben drei Felder gesetzt, die
   * `refresh()` ohnehin kennt.
   */
  useEffect(() => {
    const abmeldenReauth = onReauthRequired((message) => {
      setUser(undefined);
      setState('anonymous');
      setFailure(message);
    });
    /*
      **Die zweite Weiche, und sie ist die Antwort auf Meldung (2).** Eine
      gewöhnliche `AUTH_REQUIRED` aus IRGENDEINER Anfrage führt hierher —
      `api/client.ts` ist die einzige Stelle, die jede Antwort der API sieht.
      Ohne einen Satz im Kasten: Ein abgelaufenes Cookie hat keinen Grund, den
      man dem Anwender erklären müsste; «Die Anmeldung ist erneut nötig» über
      einer leeren Maske erklärte weniger, als die Maske selbst schon sagt.
    */
    const abmeldenAuth = onAuthRequired(() => {
      rueckkehrpfadMerken(window.location);
      setUser(undefined);
      setState('anonymous');
    });
    /*
      Die dritte Weiche: Irgendeine Anfrage lief in `SESSION_LOCKED`. Der
      Benutzer bleibt stehen — er ist angemeldet, nur verriegelt —, und der
      Sperrbildschirm holt sich `gesperrtSeit` und `geraet` über `refresh()`
      nach. Ohne dieses `refresh()` stünde dort «Unbekannt», weil die Angaben
      allein in der Antwort auf `/auth/me` stehen.
    */
    const abmeldenLock = onSessionLocked(() => {
      setState('locked');
      void refresh();
    });
    void refresh();
    return () => {
      abmeldenReauth();
      abmeldenAuth();
      abmeldenLock();
    };
  }, [refresh]);

  /**
   * Der leichte Rückruf beim Zurückkommen in den Tab.
   *
   * **Ohne ihn fiele ein abgelaufenes Cookie erst beim nächsten Klick auf** —
   * und zwar in der Maske, in die geklickt wurde, statt an der Stelle, die
   * dafür zuständig ist. Wer nach der Mittagspause seinen Tab wieder
   * hervorholt, soll den Sperrbildschirm sehen und nicht eine Tabelle, deren
   * Filterknopf beim ersten Druck einen Fehler wirft.
   *
   * `GET /auth/me` und keine eigene Sonde: Der Endpunkt liest zwei kleine,
   * indizierte Tabellen und ist ohnehin der einzige, den eine gesperrte
   * Sitzung beantwortet.
   */
  useEffect(() => {
    const beiSichtbarkeit = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (state === 'anonymous' || state === 'loading') return;
      void refresh();
    };
    document.addEventListener('visibilitychange', beiSichtbarkeit);
    return () => {
      document.removeEventListener('visibilitychange', beiSichtbarkeit);
    };
  }, [refresh, state]);

  /*
    Die Leerlaufmessung. Sie läuft NUR in der offenen Sitzung: Auf der
    Anmeldemaske gibt es nichts zu sperren, und im gesperrten Zustand nichts
    mehr zu messen.
  */
  useLeerlauf({
    fristMs: LEERLAUF_MS,
    aktiv: state === 'signed-in',
    beiLeerlauf: () => {
      setState('locked');
      // Der Server hat dieselbe Frist und dieselbe Uhr nicht; `refresh()`
      // holt seinen Stand — und damit auch `gesperrtSeit` für die
      // Kontextzeile. Sagt er «offen», bleibt der Bildschirm trotzdem
      // stehen: Der nächste Klick des Anwenders zählt dort ohnehin als
      // Aktivität, und ein Bildschirm, der von selbst wieder verschwindet,
      // sähe nach einem Fehler aus.
      void refresh();
    },
  });

  const signOut = useCallback((): void => {
    void (async () => {
      await logout();
      setUser(undefined);
      setBaumBenutzer(undefined);
      setState('anonymous');
    })();
  }, []);

  if (state === 'loading') {
    return (
      <main className="p-6">
        <Loading what="Die Sitzung" />
      </main>
    );
  }

  if (state === 'anonymous' || user === undefined) {
    /*
      **Der Hinweis geht IN die Anmeldemaske und steht nicht mehr darüber**
      (Chip `W-loginredesign`). Bis zum Umbau war die Maske eine 448 px breite
      Karte in der Bildmitte, und ein Kasten darüber schob sie ein Stück nach
      unten. Jetzt füllt sie den Bildschirm: Derselbe Kasten schöbe sie aus dem
      Bild, und der Anwender sähe eine leere Fläche mit einer Meldung, aber kein
      Formular. Die Begründung samt Vorrangregel steht bei `LoginPage.hinweis`.
    */
    return (
      <LoginPage
        {...(failure === undefined ? {} : { hinweis: failure })}
        onSignedIn={() => {
          /*
            Der EINE Ort, an dem dieses System weiss, dass sich soeben jemand
            angemeldet hat — und damit der einzige, der das Startbild fällig
            stellen kann (`splash/sitzungsmarke.ts`). Ein Neuladen, ein
            Seitenwechsel und ein Entsperren kommen hier nicht vorbei und
            bekommen deshalb keines.
          */
          splashFaelligMerken();
          void refresh();
        }}
      />
    );
  }

  const gesperrt = state === 'locked';

  /*
    **Die zwei Zweige stehen an DERSELBEN Stelle des Baumes, und das ist keine
    Formsache.** React ordnet einen Zustand seiner Stelle im Baum zu, nicht
    seiner Komponente: Ein Umbau, der `SignedIn` beim Sperren an eine andere
    Position schöbe — etwa in ein Fragment mit dem Sperrbildschirm davor —,
    hängte ihn ab und montierte ihn danach neu. Jede offene Eingabe wäre weg,
    und die Zusage im Fuss des Sperrbildschirms («Deine Eingaben und offenen
    Entwürfe bleiben erhalten») wäre eine Lüge, die keine Prüfung bemerkt, weil
    beide Bildschirme richtig aussehen.

    Deshalb: derselbe Wrapper in beiden Lagen, nur `hidden` wechselt. Und
    deshalb `baumBenutzer` statt `user` — siehe dort.
  */
  return (
    <>
      {baumBenutzer === undefined ? null : (
        <div hidden={gesperrt} aria-hidden={gesperrt || undefined}>
          <SignedIn user={baumBenutzer} onSignOut={signOut} />
        </div>
      )}
      {gesperrt ? (
        <Sperrbildschirm
          user={user}
          onEntsperrt={() => {
            void refresh();
          }}
          onAbgemeldet={() => {
            rueckkehrpfadMerken(window.location);
            setUser(undefined);
            setBaumBenutzer(undefined);
            setState('anonymous');
          }}
          onSignOut={signOut}
        />
      ) : null}
    </>
  );
}

/**
 * Die angemeldete Anwendung: Rahmen plus die Seite zur aktuellen Adresse.
 *
 * **Die Fähigkeiten werden an die Stammdatenseiten durchgereicht.** Nicht als
 * Schutz — darüber entscheidet der Wächter bei jeder Anfrage neu —, sondern
 * damit eine Maske nicht anbietet, was ihr Anwender nicht darf. Ein
 * Speichern-Knopf, der zuverlässig eine 403 liefert, ist schlechter als kein
 * Knopf: Er sieht aus wie ein Fehler des Systems und ist eine Aussage über den
 * Anwender.
 *
 * **Das Namensverzeichnis lädt hier, einmal, und wird genauso durchgereicht**
 * (`W-namensanzeige`, 2026-08-31) — dieselbe Arbeitsteilung wie bei
 * `capabilities`: kein Schutz, nur die Auflösung einer Akteurs-UUID zu einem
 * Namen für Seiten, die eine zeigen. Die eigene Zeile steht sofort fest, ohne
 * auf die Antwort zu warten — `/auth/me` liefert `displayName` schon mit,
 * und der häufigste Fall («von Ihnen angelegt») zeigte sonst kurz die UUID,
 * bis der zweite Abruf zurück ist.
 */
function SignedIn(props: { readonly user: Me; readonly onSignOut: () => void }): ReactNode {
  const echteNavigation = useNavigation();
  const capabilities = useMemo(() => new Set(props.user.capabilities), [props.user.capabilities]);

  /*
    **Die Schalenregel der offenen Einrichtung** (`PF-assistent`, Spec 6.2 und
    6.5, `E-1653`). Ist die Ersteinrichtung offen, führt die Schale jede Adresse
    ausser dem Assistenten auf den Assistenten: Wer die Fähigkeit hat, wird auf
    die Adresse umgeleitet (`replace`, damit der Zurück-Knopf nicht in eine
    Seite zurückführt, die gleich wieder umbiegt); wer sie nicht hat, sieht an
    derselben Stelle den Hinweis, dass die Einrichtung noch läuft. Die Adresse
    der Seite (`route`) ist dabei die wirksame und nicht die getippte — Menüpunkt,
    Brotkrumen und Seite zeigen einen Ort, nicht zwei.

    **Ohne Antwort verhält sich die Schale wie auf einer eingerichteten
    Installation** (`einrichtungslage.ts`): Auf der eigenen Installation, die
    abgeschlossen ist, ändert sich damit nichts.
  */
  const { lage, aktualisieren } = useEinrichtungslage();
  const einrichtungOffen = lage === 'offen';
  const darfEinrichten = capabilities.has('einstellung:manage');
  const ausserhalb = einrichtungOffen && echteNavigation.route.name !== 'einrichtung';
  const navigation = ausserhalb ? { ...echteNavigation, route: EINRICHTUNG_ROUTE } : echteNavigation;
  const route = navigation.route;
  useEffect(() => {
    if (ausserhalb && darfEinrichten) echteNavigation.replace(EINRICHTUNG_ROUTE);
  }, [ausserhalb, darfEinrichten, echteNavigation]);

  const [verzeichnis, setVerzeichnis] = useState<Verzeichnis>(
    () => new Map([[props.user.userId, props.user.displayName]]),
  );

  /*
    Das Startbild (`W-splash`). Gelesen wird die Marke im ANFANGSWERT, damit
    die Fläche schon im ersten Renderdurchgang steht und die Anwendung
    darunter nicht für einen Lidschlag sichtbar wird; verworfen wird sie in
    einem Effekt, weil das Rendern nichts ausserhalb von React verändern soll.
    Die Trennung und ihr Grund stehen in `splash/sitzungsmarke.ts`.

    Es hängt NEBEN der Schale und nicht in ihr: Die Anwendung rendert und lädt
    darunter vollständig weiter, und wenn die Zeitachse durch ist, ist sie
    schon da. Ein Startbild, das den Aufbau anhielte, machte aus einer Anzeige
    eine Wartezeit.
  */
  const [splashLaeuft, setSplashLaeuft] = useState(splashFaelligLesen);
  useEffect(() => {
    splashMarkeVerwerfen();
  }, []);
  const splashBeenden = useCallback((): void => {
    setSplashLaeuft(false);
  }, []);

  useEffect(() => {
    if (!capabilities.has('user:read')) return;
    void (async () => {
      try {
        const users = await listUsers();
        setVerzeichnis((bisherig) => {
          const naechstes = new Map(bisherig);
          for (const user of users) naechstes.set(user.id, user.displayName);
          return naechstes;
        });
      } catch {
        // Bleibt bei der Selbstzeile stehen; Seiten fallen für jede
        // unbekannte Kennung auf die UUID zurück (`benutzername()`).
      }
    })();
  }, [capabilities]);

  return (
    <>
    <Shell user={props.user} navigation={navigation} onSignOut={props.onSignOut}>
      {/* Eine gemeinsame Ladeanzeige für alle Seiten — es rendert ohnehin
          immer genau ein Zweig, nie mehrere zugleich. Der Wechsel von einer
          Adresse zur anderen zeigt so kurz «Die Seite wird geladen …» statt
          eines leeren Rahmens, bis der Seiten-Chunk da ist; beim ersten
          Besuch einer Adresse ist das ein echter Netzwerkschritt, danach
          liegt der Chunk im Browser-Cache. */}
      <Suspense fallback={<Loading what="Die Seite" />}>
        {/* `anzeigename` speist allein die Begrüssung des Dashboard-Kopfes
            (`F-dashlayout`) und ist derselbe Wert, den `/auth/me` ohnehin
            mitliefert — kein zweiter Abruf, und keine geratene Anrede: Ohne
            die Angabe grüsst der Kopf namenlos. */}
        {route.name === 'overview' ? (
          <UebersichtPage
            navigate={navigation.navigate}
            anzeigename={props.user.displayName}
          />
        ) : null}
        {/* Die Suchseite bekommt einen `key` aus der Anfrage: Sie schreibt ihre
            eigene Adresse ueber `replaceState` fort (der Router sieht das
            nicht), und ein echter Adresswechsel -- Menueklick, `⌘⏎` aus dem
            Overlay, Zurueck-Knopf -- soll die Maske dann wirklich mit der neuen
            Frage neu aufsetzen statt die alte Eingabe stehen zu lassen. */}
        {route.name === 'suche' ? (
          <SuchePage key={route.q} q={route.q} navigate={navigation.navigate} />
        ) : null}
        {route.name === 'journal' ? <JournalPage navigate={navigation.navigate} /> : null}
        {route.name === 'entry' ? (
          <EntryPage id={route.id} navigate={navigation.navigate} verzeichnis={verzeichnis} />
        ) : null}
        {route.name === 'periods' ? <PeriodsPage verzeichnis={verzeichnis} /> : null}
        {route.name === 'accounts' ? (
          <AccountsPage capabilities={capabilities} navigate={navigation.navigate} />
        ) : null}
        {route.name === 'account-ledger' ? (
          <AccountLedgerPage
            number={route.number}
            navigate={navigation.navigate}
            replace={navigation.replace}
            {...(route.from === undefined ? {} : { from: route.from })}
            {...(route.to === undefined ? {} : { to: route.to })}
          />
        ) : null}
        {route.name === 'profile' ? <ProfilePage user={props.user} /> : null}
        {/* Ohne Fähigkeiten als Eigenschaft: Die Seite bietet nichts an, was
          eine Fähigkeit voraussetzte — sie zeigt, und der Wächter entscheidet
          bei ihrem einen Abruf. Wer `system:read` nicht hat, sieht den
          Menüpunkt gar nicht erst und bekäme hier eine 403 in der Fehlerbox. */}
        {route.name === 'system' ? <SystemstatusPage /> : null}
        {/* Wie bei `system` zwei Zeilen darueber: keine Fähigkeiten als
          Eigenschaft, die Seite zeigt nur, und der Wächter entscheidet bei
          ihrem einen Abruf. */}
        {route.name === 'regeln' ? (
          <RegelnPage capabilities={capabilities} verzeichnis={verzeichnis} />
        ) : null}
        {/* Wie `system` und `regeln`: keine Fähigkeiten als Eigenschaft. Die
          Seite liest ausschliesslich, und der Wächter entscheidet bei ihrem
          Abruf. */}
        {route.name === 'anfragen' ? <AnfragenPage /> : null}
        {route.name === 'users' ? (
          <UsersPage capabilities={capabilities} eigenerId={props.user.userId} />
        ) : null}
        {/* Mit Verzeichnis, anders als `UsersPage`: Die Tokenliste zeigt
          `createdBy`/`revokedBy` als Akteurs-Kennung und braucht dafuer die
          Namensaufloesung (`W-namensanzeige`) — die Benutzerliste zeigt den
          Namen ohnehin in ihrer eigenen Zeile. */}
        {route.name === 'api-tokens' ? (
          <ApiTokensPage capabilities={capabilities} verzeichnis={verzeichnis} />
        ) : null}
        {route.name === 'lizenz' ? <LizenzPage capabilities={capabilities} /> : null}
        {/* Mit Verzeichnis: Das beendete Ziel nennt die Person, die es beendet hat
          (`D-43`), und eine UUID unter einem Datum ist keine Auskunft
          (`W-namensanzeige`). */}
        {route.name === 'sicherung' ? (
          <DatensicherungPage capabilities={capabilities} verzeichnis={verzeichnis} />
        ) : null}
        {/* Mit Verzeichnis wie die Tokenliste: Die Endpunktliste nennt keinen
          Akteur, aber der Starttag der Aufladungsautomatik traegt
          `gesetztVon` als Kennung, und eine UUID unter einem Datum ist keine
          Auskunft (`W-namensanzeige`). */}
        {route.name === 'webhooks' ? (
          <WebhooksPage capabilities={capabilities} verzeichnis={verzeichnis} />
        ) : null}
        {route.name === 'lastschrift' ? <LastschriftPage capabilities={capabilities} /> : null}
        {/* Mit Verzeichnis wie die Endpunktliste: Eine erledigte Zeile trägt
          `erledigtVon` als Kennung, und eine UUID neben einem Datum ist keine
          Auskunft (`W-namensanzeige`). */}
        {route.name === 'portal-vorgaenge' ? (
          <PortalVorgaengePage
            capabilities={capabilities}
            verzeichnis={verzeichnis}
            navigate={navigation.navigate}
          />
        ) : null}
        {route.name === 'customers' ? (
          <CustomersPage navigate={navigation.navigate} capabilities={capabilities} />
        ) : null}
        {route.name === 'customer-new' ? <CustomerForm navigate={navigation.navigate} /> : null}
        {route.name === 'customer' ? (
          <CustomerPage id={route.id} navigate={navigation.navigate} capabilities={capabilities} />
        ) : null}
        {route.name === 'duplicates' ? (
          <DuplicatesPage navigate={navigation.navigate} capabilities={capabilities} />
        ) : null}
        {route.name === 'vendors' ? (
          <VendorsPage navigate={navigation.navigate} capabilities={capabilities} />
        ) : null}
        {route.name === 'vendor-new' ? <VendorForm navigate={navigation.navigate} /> : null}
        {route.name === 'vendor' ? (
          <VendorPage id={route.id} navigate={navigation.navigate} capabilities={capabilities} />
        ) : null}
        {route.name === 'documents' ? (
          <DocumentsPage
            navigate={navigation.navigate}
            replace={navigation.replace}
            capabilities={capabilities}
            {...(route.tab === undefined ? {} : { tab: route.tab })}
          />
        ) : null}
        {route.name === 'document-new' ? <DocumentForm navigate={navigation.navigate} /> : null}
        {/* Die Belegansicht bekommt den ganzen Benutzer und nicht nur seine
          Fähigkeiten: Das Vier-Augen-Prinzip vergleicht den Ersteller des Belegs
          mit dem Angemeldeten, und dafür braucht sie die Benutzerkennung. */}
        {route.name === 'document' ? (
          <DocumentPage
            id={route.id}
            navigate={navigation.navigate}
            user={props.user}
            capabilities={capabilities}
            verzeichnis={verzeichnis}
          />
        ) : null}
        {/* Die drei Adressen der Aborechnungsvorlagen. `capabilities` gehen
          durch, damit die Masken nicht anbieten, was ihr Anwender nicht
          darf — mit EINER benannten Ausnahme: Der Weg nach `aktiv` erscheint
          auch ohne `aborechnung:scharfschalten`, dann gesperrt und mit Grund
          (Begründung im Kopfkommentar von `StatusSchalter.tsx`). */}
        {route.name === 'aborechnungen' ? (
          <AborechnungenPage navigate={navigation.navigate} capabilities={capabilities} />
        ) : null}
        {route.name === 'aborechnung-new' ? (
          <AborechnungForm navigate={navigation.navigate} />
        ) : null}
        {route.name === 'aborechnung' ? (
          <AborechnungPage
            id={route.id}
            navigate={navigation.navigate}
            capabilities={capabilities}
          />
        ) : null}
        {route.name === 'einrichtung' ? (
          <EinrichtungPage
            capabilities={capabilities}
            lage={lage}
            onAbgeschlossen={aktualisieren}
          />
        ) : null}
        {route.name === 'vorsystem' ? (
          <UebernahmePage capabilities={capabilities} navigate={navigation.navigate} />
        ) : null}
        {route.name === 'number-ranges' ? <NumberRangesPage capabilities={capabilities} /> : null}
        {route.name === 'issuer' ? <IssuerPage capabilities={capabilities} /> : null}
        {/* Mit Fähigkeiten UND Verzeichnis: Die Seite entscheidet über zwei
          eigene Fähigkeiten (`mailvorlage:manage`, `mailvorlage:scharfschalten`),
          und ihre Fassungshistorie zeigt `createdBy`/`scharfgeschaltetVon` als
          Akteurskennung — dafür braucht sie die Namensauflösung
          (`W-namensanzeige`), dieselbe Kombination wie bei `RegelnPage`. */}
        {route.name === 'mailvorlagen' ? (
          <MailvorlagenPage capabilities={capabilities} verzeichnis={verzeichnis} />
        ) : null}
        {route.name === 'belegvorlagen' ? (
          <BelegvorlagenPage
            capabilities={capabilities}
            verzeichnis={verzeichnis}
            navigate={navigation.navigate}
          />
        ) : null}
        {route.name === 'bank-accounts' ? (
          <BankAccountsPage
            navigate={navigation.navigate}
            capabilities={capabilities}
            neu={route.neu === true}
          />
        ) : null}
        {route.name === 'bank-account' ? (
          <BankAccountPage
            id={route.id}
            navigate={navigation.navigate}
            replace={navigation.replace}
            capabilities={capabilities}
            {...(route.from === undefined ? {} : { from: route.from })}
            {...(route.to === undefined ? {} : { to: route.to })}
          />
        ) : null}
        {route.name === 'bank-import' ? (
          <BankImportPage
            id={route.id}
            navigate={navigation.navigate}
            capabilities={capabilities}
          />
        ) : null}
        {route.name === 'bank-statements' ? (
          <BankStatementsPage
            id={route.id}
            navigate={navigation.navigate}
            capabilities={capabilities}
          />
        ) : null}
        {route.name === 'zahldienst-abruf' ? (
          <AbrufPage id={route.id} navigate={navigation.navigate} capabilities={capabilities} />
        ) : null}
        {route.name === 'zahldienst-klaerliste' ? (
          <Klaerliste
            accountId={route.id}
            navigate={navigation.navigate}
            replace={navigation.replace}
            userId={props.user.userId}
            verzeichnis={verzeichnis}
            capabilities={capabilities}
          />
        ) : null}
        {route.name === 'klaerliste' ? (
          <Klaerliste
            navigate={navigation.navigate}
            replace={navigation.replace}
            userId={props.user.userId}
            verzeichnis={verzeichnis}
            capabilities={capabilities}
            {...(route.konto === undefined ? {} : { accountId: route.konto })}
          />
        ) : null}
        {/*
          Die Klaerungsliste der Zahlungszuordnung (`ZZ-web`, Task 7) — eine
          EIGENE Seite neben `klaerliste` darueber und ausdruecklich nicht
          dieselbe Komponente (`D-26`).
        */}
        {route.name === 'zahlungsklaerung' ? (
          <KlaerungPage capabilities={capabilities} navigate={navigation.navigate} />
        ) : null}
        {/*
          Die Freigabeliste der Zahlungszuordnungen (`ZZ-freigabeliste`) —
          wieder eine eigene Seite: Jene zeigt Bewegungen OHNE Zuordnung,
          diese Zuordnungen ohne Freigabe. Sie traegt zugleich den
          Guthaben-Handweg (`D-28`) und blendet beide Abschnitte je Faehigkeit
          aus, nach dem Muster von `ErstattungenPage`.
        */}
        {route.name === 'zahlungsfreigabe' ? (
          <ZahlungFreigabePage
            userId={props.user.userId}
            verzeichnis={verzeichnis}
            capabilities={capabilities}
          />
        ) : null}
        {route.name === 'kontieren' ? (
          <UnkontiertPage
            id={route.id}
            capabilities={capabilities}
            userId={props.user.userId}
            navigate={navigation.navigate}
            {...(route.bewegungId === undefined ? {} : { bewegungId: route.bewegungId })}
          />
        ) : null}
        {route.name === 'eingang' ? (
          <EingangskorbPage
            navigate={navigation.navigate}
            capabilities={capabilities}
            neu={route.neu === true}
          />
        ) : null}
        {route.name === 'eingang-erfassen' ? (
          <ErfassungPage
            // Erzwingt einen sauberen Neustart beim Wechsel zwischen zwei
            // Belegen: Ohne eigenen `key` bliebe die Komponenteninstanz beim
            // Navigieren von einem wiedergeöffneten Beleg zum nächsten
            // bestehen, und ihr Formularzustand liefe dem neuen Beleg voraus.
            key={route.invoiceId ?? route.id ?? 'neu'}
            id={route.id}
            {...(route.invoiceId === undefined ? {} : { invoiceId: route.invoiceId })}
            navigate={navigation.navigate}
            capabilities={capabilities}
            // Nicht als Schutz, sondern damit die Maske sagen kann, WER erfasst
            // hat: Wer eine Eingangsrechnung erfasst, gibt sie nicht frei
            // (`D-22`) — der Knopf ist dann gesperrt und erklärt sich.
            userId={props.user.userId}
          />
        ) : null}
        {route.name === 'kreditorenerwartung' ? <KreditorenerwartungPage /> : null}
        {route.name === 'guthaben-abstimmung' ? (
          <AbstimmungPage navigate={navigation.navigate} />
        ) : null}
        {route.name === 'guthaben-abstimmung-detail' ? (
          <AbstimmungDetailPage
            id={route.id}
            navigate={navigation.navigate}
            verzeichnis={verzeichnis}
          />
        ) : null}
        {route.name === 'offene-posten' ? (
          <OffenePostenPage navigate={navigation.navigate} />
        ) : null}
        {route.name === 'guthaben-erstattungen' ? (
          <ErstattungenPage userId={props.user.userId} verzeichnis={verzeichnis} capabilities={capabilities} />
        ) : null}
        {/*
          Guthaben-Korrekturen (`GU-korrekturmaske`) — die Liste und die
          Freigabe. Dieselben drei Eigenschaften wie bei den Erstattungen
          darueber: `userId` fuer die «von Ihnen»-Markierung, `verzeichnis`
          fuer die Namen von Anleger und Freigeber, `capabilities` fuer die
          zwei Abschnitte, die die Seite je Faehigkeit ausblendet.
        */}
        {route.name === 'guthaben-korrekturen' ? (
          <KorrekturenPage
            userId={props.user.userId}
            verzeichnis={verzeichnis}
            capabilities={capabilities}
          />
        ) : null}
        {route.name === 'datev-export' ? (
          // `userId` nicht als Schutz, sondern damit die Maske sagen kann, WER
          // erzeugt hat: Wer erzeugt, gibt nicht frei (`D-21`) -- der
          // Freigabeknopf ist dann gesperrt und erklaert sich, statt zu
          // fehlen. Dieselbe Begruendung wie bei der Erfassungsmaske oben.
          <DatevExportPage
            userId={props.user.userId}
            verzeichnis={verzeichnis}
            capabilities={capabilities}
          />
        ) : null}
        {route.name === 'geldkonto-anfangsbestand' ? (
          <GeldkontoAnfangsbestandPage capabilities={capabilities} />
        ) : null}
        {route.name === 'saldenvortraege' ? (
          <SaldenvortraegePage
            capabilities={capabilities}
            userId={props.user.userId}
            verzeichnis={verzeichnis}
          />
        ) : null}
        {/*
          Die Auswertungen -- acht Adressen, eine Komponente. Sie bekommt die
          Adresse selbst und nicht acht Kopien dieser Zeile: Welcher Reiter
          aktiv ist, ist eine Frage AN die Seite und keine, die `App.tsx`
          achtmal beantwortet.
        */}
        {istAuswertungsadresse(route) ? (
          <AuswertungenIndexPage route={route} navigate={navigation.navigate} />
        ) : null}
      </Suspense>
    </Shell>
    {splashLaeuft ? (
      <SplashScreen
        anzeigename={props.user.displayName}
        version={SPLASH_FASSUNGSZEILE}
        onFertig={splashBeenden}
      />
    ) : null}
    </>
  );
}
